"use client"

import Link from "next/link"
import { useTranslations } from "next-intl"
import { LogOut, User } from "lucide-react"

import { useUser, useUserActions } from "@/context/UserContext"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"

/**
 * Who is signed in, and the two things you do about it.
 *
 * ── What this menu is for ────────────────────────────────────────────────────────────────
 *
 * Your name, your account, and the way out. Nothing else — a menu behind an avatar is where
 * options go to be forgotten, so anything that belongs on a page belongs on the page.
 *
 * It used to carry three more groups, and each has somewhere better to be:
 *
 *   · A LIGHT/DARK/SYSTEM list. The header has a theme toggle sitting a couple of controls to
 *     the left of this one. Two controls for one setting is worse than either alone, and the one
 *     that is always visible wins over the one behind a click.
 *   · Subscribe / Manage subscription, branched on tier. Both went to a page — `/pricing` and
 *     `/account` — so the menu was a second route to somewhere that already has one, and it had
 *     to know about tiers to choose which.
 *   · Preferences, and a disabled "Experimental features" that had been commented out for long
 *     enough to have stopped meaning anything. Preferences is still reachable from the command
 *     menu, which is where someone looking for a setting by name actually goes.
 */
export function UserButton() {
  const t = useTranslations("nav.UserButton")
  const user = useUser()
  const { signOut } = useUserActions()

  // Signed out this is not a menu at all — there is nothing to put in it. It is the way in.
  if (!user) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="default" size="icon" className="rounded-full" asChild>
            <Link href="/sign-in" aria-label={t("signIn")}>
              <User />
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("signIn")}</TooltipContent>
      </Tooltip>
    )
  }

  const name = [user.first_name, user.last_name].filter(Boolean).join(" ")

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="default" size="icon" aria-label={name || t("account")}>
          {user.first_name?.charAt(0)}
          {user.last_name?.charAt(0)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56" side="bottom" align="end" sideOffset={16}>
        {/*
          The name identifies the account rather than offering anything, which is what a label is
          for — it is not focusable and cannot be activated, so it does not sit in the keyboard
          path between the trigger and the two rows that ARE actions.

          Falls back to the account label: the name fields are optional on a profile, and an
          empty heading over a menu reads as a rendering fault.
        */}
        <DropdownMenuLabel>{name || t("account")}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/*
          `asChild`, so the item IS the link.

          Wrapping a `DropdownMenuItem` in a `Link` — which is what every row here used to do —
          puts the anchor outside the menu item, so Radix's roving focus lands on the item while
          the thing that navigates is its parent. Enter then does nothing, and the row is
          reachable by pointer only.
        */}
        <DropdownMenuItem asChild>
          <Link href="/account">
            <User/>
            {t("account")}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={signOut}>
          <LogOut/>
          {t("signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
